import type { PlainMessage } from '@bufbuild/protobuf';

import {
  NoteBlockWidget,
  NoteContextKey,
  type NoteChoiceBlock,
  type NoteCodeSystem,
  type NoteList,
  type NoteOption,
  type NoteTableBlock,
  type NoteTableColumn,
  type NoteTableRow,
  type NoteTeethBlock,
  type NoteToothCode,
} from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';

import {
  createTempId,
  isNoteOptionCode,
  listNoteBlockOptions,
  noteChoiceLabel,
  noteOptionValue,
  noteValueType,
  splitInlineGuidance,
  type DocumentSectionSpec,
  type DocumentTemplate,
  type NoteValueType,
  type TemplateIssue,
} from '@/entities/templates';

import type { NoteLinkOpener, NoteLinkSections } from './blockEditorView';
import {
  describeNoteLine,
  getNoteBlockValues,
  normalizeNoteValue,
  type NoteBlockDef,
  type NoteLineDef,
} from './noteView';
import type { TemplateDraft } from './templateDraft';

// The block template editor works on the generated section and line shapes.
// New elements carry `tmp_` ids; scribe mints the real ones and returns them,
// and it alone applies defaults and validation.

// Scribe's non-blocking issues are the `warning_*` kinds.
export const isTemplateWarning = (issue: TemplateIssue) =>
  issue.Kind.startsWith('warning_');

// The kinds of field AI fills that only wording or a label names; a list
// is named by its list and a record value by its key.
const NAMED_FIELD_KINDS: NoteBlockDef['Kind']['case'][] = [
  'Choice',
  'MultiChoice',
  'Teeth',
  'FreeText',
  'Date',
  'Number',
  'Table',
];

const isWording = (block: NoteBlockDef) =>
  block.Kind.case === 'Text' &&
  !/^[\s.,;:!?…%)]*$/.test(block.Kind.value.Value);

// The fields of a line with neither wording before them, a label nor an AI
// instruction: AI then hardly knows what they take, which the editor warns
// of softly.
export const listUnnamedNoteFields = (line: NoteLineDef) =>
  line.Blocks.flatMap((block, index) =>
    NAMED_FIELD_KINDS.includes(block.Kind.case) &&
    !(block.Kind.value as { Label?: string }).Label?.trim() &&
    !block.AIInstruction?.trim() &&
    !line.Blocks.slice(0, index).some(isWording)
      ? [block.ID]
      : [],
  );

const NOTE_SLOT_ID = /^[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z0-9_]+)*$/;

const NOTE_SLOT_ID_MAX_LENGTH = 64;

// Scribe's `IsNoteSlotID`: a field's code is a letter, then letters, digits
// and underscores, in dot-separated parts, 64 characters at most.
export const isNoteSlotId = (code: string) =>
  code.length <= NOTE_SLOT_ID_MAX_LENGTH && NOTE_SLOT_ID.test(code);

// A choice takes one value or several, a teeth field one tooth or several;
// what it offers and how it shows them stay as they are.
export const setNoteBlockMultiple = (
  block: NoteBlockDef,
  multiple: boolean,
): NoteBlockDef => {
  switch (block.Kind.case) {
    case 'Choice':
    case 'MultiChoice':
      return {
        ...block,
        Kind: multiple
          ? { case: 'MultiChoice', value: block.Kind.value }
          : { case: 'Choice', value: block.Kind.value },
      };
    case 'ListRef':
      return {
        ...block,
        Kind: {
          case: 'ListRef',
          value: { ...block.Kind.value, Multiple: multiple },
        },
      };
    case 'Teeth':
      return {
        ...block,
        Kind: {
          case: 'Teeth',
          value: { ...block.Kind.value, Multiple: multiple },
        },
      };
    default:
      return block;
  }
};

// The element types of the editor, each with its label and icon. Options
// cover one or several values, typed in the block or read from a clinic list;
// they are stored as a choice, a multi-choice or a list reference.
export type NoteBlockPaletteKind =
  | 'text'
  | 'options'
  | 'teeth'
  | 'freeText'
  | 'date'
  | 'number'
  | 'table'
  | 'context';

// A field the editor adds, by the value type it holds: the element type that
// names it and what a new one starts as.
type NoteFieldType = {
  valueType: NoteValueType;
  paletteKind: NoteBlockPaletteKind;
  createKind: () => NoteBlockDef['Kind'];
};

export const NOTE_FIELD_TYPES: NoteFieldType[] = [
  {
    valueType: 'choice',
    paletteKind: 'options',
    createKind: () => ({
      case: 'Choice',
      value: {
        Values: [],
        OtherAllowed: false,
        Widget: NoteBlockWidget.NoteBlockWidgetButtons,
        Options: [],
      },
    }),
  },
  {
    valueType: 'teeth',
    paletteKind: 'teeth',
    createKind: () => ({
      case: 'Teeth',
      value: { Groups: [], Teeth: [], Dentition: 0, Granularity: 0, Codes: [] },
    }),
  },
  {
    valueType: 'text',
    paletteKind: 'freeText',
    createKind: () => ({ case: 'FreeText', value: {} }),
  },
  {
    valueType: 'date',
    paletteKind: 'date',
    createKind: () => ({ case: 'Date', value: {} }),
  },
  {
    valueType: 'number',
    paletteKind: 'number',
    createKind: () => ({ case: 'Number', value: { Qualifiers: [] } }),
  },
  {
    valueType: 'table',
    paletteKind: 'table',
    // Its columns, and rows unless the note adds them, are set in its
    // settings, which a new block opens.
    createKind: () => ({
      case: 'Table',
      value: { Columns: [], Rows: [], AddRows: true },
    }),
  },
];

// A value from the record is named as one whatever it holds.
export const getNoteBlockPaletteKind = (
  block: NoteBlockDef,
): NoteBlockPaletteKind => {
  if (block.Kind.case === 'Context') {
    return 'context';
  }
  const valueType = noteValueType(block);
  return (
    NOTE_FIELD_TYPES.find((field) => field.valueType === valueType)
      ?.paletteKind ?? 'text'
  );
};

// A list code is unique per clinic and language and never changes; the name
// is what people read.
export const createNoteListCode = () => `list_${createTempId().slice(4, 12)}`;

const createNoteBlockKind = (
  kind: NoteBlockPaletteKind,
): NoteBlockDef['Kind'] => {
  if (kind === 'context') {
    return {
      case: 'Context',
      value: { Key: NoteContextKey.NoteContextKeyClinicianName },
    };
  }
  const field = NOTE_FIELD_TYPES.find((entry) => entry.paletteKind === kind);
  return field ? field.createKind() : { case: 'Text', value: { Value: '' } };
};

export const createNoteBlock = (kind: NoteBlockPaletteKind): NoteBlockDef => ({
  ID: createTempId(),
  SlotID: '',
  Kind: createNoteBlockKind(kind),
});

export const createNoteLine = (block: NoteBlockDef): NoteLineDef => ({
  ID: createTempId(),
  Blocks: [block],
  ShowWhen: [],
});

export const createNoteSection = (title: string): DocumentSectionSpec => ({
  ID: createTempId(),
  Code: `section_${crypto.randomUUID().replaceAll('-', '').slice(0, 12)}`,
  Title: title,
  EditorType: '',
  Type: '',
  ProcessingType: '',
  Options: [],
  TemplateType: '',
  Required: false,
  Lines: [createNoteLine(createNoteBlock('text'))],
});

const withSections = (
  draft: TemplateDraft,
  sections: DocumentSectionSpec[],
): TemplateDraft => ({
  ...draft,
  template: { ...draft.template, sections },
});

// Links that named a removed block go with it; a line left without blocks
// goes too.
export const dropNoteLinksTo = (draft: TemplateDraft, blockIds: string[]) =>
  withSections(
    draft,
    draft.template.sections.map((section) => ({
      ...section,
      Lines: section.Lines.map((line) =>
        line.ShowWhen.some((condition) => blockIds.includes(condition.BlockID))
          ? {
              ...line,
              ShowWhen: line.ShowWhen.filter(
                (condition) => !blockIds.includes(condition.BlockID),
              ),
            }
          : line,
      ),
    })),
  );

export type NoteBlockPlace = {
  section: DocumentSectionSpec;
  line: NoteLineDef;
  block: NoteBlockDef;
  index: number;
};

export const findNoteBlock = (
  template: DocumentTemplate,
  blockId: string,
): NoteBlockPlace | undefined => {
  for (const section of template.sections) {
    for (const line of section.Lines) {
      const index = line.Blocks.findIndex((block) => block.ID === blockId);
      if (index >= 0) {
        return { section, line, block: line.Blocks[index], index };
      }
    }
  }
  return undefined;
};

// The option that opens a line, by its label: a stored code is not what the
// template reads.
export const nameNoteLinkOpener = (
  template: NoteLinkSections,
  { blockId, value }: Pick<NoteLinkOpener, 'blockId' | 'value'>,
) => {
  const trigger = template.sections
    .flatMap((section) => section.Lines)
    .flatMap((line) => line.Blocks)
    .find((block) => block.ID === blockId);
  return trigger ? noteChoiceLabel(trigger, value) : value;
};

// A copy is a new block: scribe mints its id, and no line depends on it
// until a link names it.
export const cloneNoteBlock = (block: NoteBlockDef): NoteBlockDef => ({
  ...structuredClone(block),
  ID: createTempId(),
  SlotID: '',
});

// A choice block with settings of its kind changed; another block as it is.
export const updateNoteChoice = (
  block: NoteBlockDef,
  patch: Partial<PlainMessage<NoteChoiceBlock>>,
): NoteBlockDef => {
  switch (block.Kind.case) {
    case 'Choice':
      return {
        ...block,
        Kind: { case: 'Choice', value: { ...block.Kind.value, ...patch } },
      };
    case 'MultiChoice':
      return {
        ...block,
        Kind: {
          case: 'MultiChoice',
          value: { ...block.Kind.value, ...patch },
        },
      };
    default:
      return block;
  }
};

// A block's own values; a clinic-list block reads its values from the list.
export const setNoteChoiceValues = (block: NoteBlockDef, Values: string[]) =>
  updateNoteChoice(block, { Values });

// How a value a block stored reads after an edit of its options or codes:
// as it is, as what it became, or null when it went.
export type NoteValueMap = (value: string) => string | null;

// What became of the values `before` stored, by the index each item of
// `after` had in it (null for one just added): a kept item's value follows
// it, a dropped one's goes.
const mapCodedValues = <T>(
  before: T[],
  after: T[],
  kept: (number | null)[],
  value: (item: T) => string,
): NoteValueMap => {
  const renamed = new Map<string, string>();
  const dropped = new Set(before.map(value));
  kept.forEach((from, index) => {
    if (from === null || !before[from]) {
      return;
    }
    const was = value(before[from]);
    dropped.delete(was);
    if (was !== value(after[index])) {
      renamed.set(was, value(after[index]));
    }
  });
  return (stored) =>
    renamed.get(stored) ?? (dropped.has(stored) ? null : stored);
};

// The start a block keeps once its values are mapped; none when nothing is
// left of it, as scribe refuses an empty one.
const mapDefaultValues = (
  Default: NoteBlockDef['Default'],
  map: NoteValueMap,
): NoteBlockDef['Default'] => {
  if (!Default) {
    return undefined;
  }
  const next = {
    ...Default,
    Values: Default.Values.flatMap((value) => map(value) ?? []),
    Marks: Default.Marks.flatMap((mark) => {
      const Codes = mark.Codes.flatMap((code) => map(code) ?? []);
      return Codes.length || mark.Number || !mark.Codes.length
        ? [{ ...mark, Codes }]
        : [];
    }),
  };
  return next.Values.length ||
    next.Text ||
    next.None ||
    next.Marks.length ||
    next.Teeth.length ||
    next.TeethGroups.length ||
    next.Date ||
    next.Numbers.length ||
    next.TableRows.length
    ? next
    : undefined;
};

// A choice's options after an edit, `kept` giving the index each had
// (null for one just added). Values become their labels, as scribe writes
// them; what the block stores for an option, its code or else its label,
// follows it in the block's start, and `map` carries it to the links that
// name the block. A dropped option's value goes from both.
export const setNoteChoiceOptions = (
  block: NoteBlockDef,
  Options: PlainMessage<NoteOption>[],
  kept: (number | null)[],
): { block: NoteBlockDef; map: NoteValueMap } => {
  const map = mapCodedValues(
    listNoteBlockOptions(block),
    Options,
    kept,
    noteOptionValue,
  );
  return {
    block: {
      ...updateNoteChoice(block, {
        Options,
        Values: Options.map((option) => option.Label),
      }),
      Default: mapDefaultValues(block.Default, map),
    },
    map,
  };
};

// A teeth field's codes after an edit, as setNoteChoiceOptions: the marks of
// its start follow a code that changed and lose one that went.
export const setNoteTeethCodes = (
  block: NoteBlockDef,
  Codes: PlainMessage<NoteToothCode>[],
  kept: (number | null)[],
): NoteBlockDef => {
  if (block.Kind.case !== 'Teeth') {
    return block;
  }
  const map = mapCodedValues(
    block.Kind.value.Codes,
    Codes,
    kept,
    (code) => code.Code,
  );
  return {
    ...block,
    Kind: { case: 'Teeth', value: { ...block.Kind.value, Codes } },
    Default: mapDefaultValues(block.Default, map),
  };
};

// What a teeth field holds on a tooth or a surface: the tooth alone, its
// codes, or a number in a unit.
export type NoteTeethHolds = 'teeth' | 'codes' | 'number';

export const NOTE_TEETH_HOLDS: NoteTeethHolds[] = ['teeth', 'codes', 'number'];

// A number per tooth starts in millimetres, as a pocket depth is.
const NOTE_TEETH_UNIT = 'mm';

export const getNoteTeethHolds = (
  block: Pick<PlainMessage<NoteTeethBlock>, 'Codes' | 'Unit'>,
): NoteTeethHolds => {
  if (block.Unit) {
    return 'number';
  }
  return block.Codes.length ? 'codes' : 'teeth';
};

// Codes and a number exclude each other; the start the field had no longer
// fits, so it goes.
export const setNoteTeethHolds = (
  block: NoteBlockDef,
  holds: NoteTeethHolds,
): NoteBlockDef => {
  if (block.Kind.case !== 'Teeth') {
    return block;
  }
  const value = block.Kind.value;
  return {
    ...block,
    Kind: {
      case: 'Teeth',
      value: {
        ...value,
        Codes: holds === 'codes' ? value.Codes : [],
        OneCodePerTooth: holds === 'codes' ? value.OneCodePerTooth : undefined,
        Unit: holds === 'number' ? value.Unit || NOTE_TEETH_UNIT : undefined,
        Precision: holds === 'number' ? value.Precision : undefined,
      },
    },
    Default: undefined,
  };
};

type TableKind = PlainMessage<NoteTableBlock>;

// The next free neutral code of a table's columns ("c3") or rows ("r2").
export const nextNoteTableCode = (
  prefix: string,
  items: { Code: string }[],
) => {
  let number = items.length + 1;
  while (items.some((item) => item.Code === `${prefix}${number}`)) {
    number += 1;
  }
  return `${prefix}${number}`;
};

const sameValue: NoteValueMap = (value) => value;

// A table as edited, the cells of its start following their row and
// column: a code that changed carries them, one that went drops them.
const withTableStart = (
  block: NoteBlockDef,
  value: TableKind,
  rowOf: NoteValueMap,
  columnOf: NoteValueMap,
): NoteBlockDef => {
  const TableRows = (block.Default?.TableRows ?? []).flatMap((row) => {
    // An added row's id is the note's, never a code.
    const Row = value.AddRows ? row.Row : rowOf(row.Row);
    if (Row === null) {
      return [];
    }
    return [
      {
        Row,
        Cells: row.Cells.flatMap((cell) => {
          const Column = columnOf(cell.Column);
          return Column === null ? [] : [{ ...cell, Column }];
        }),
      },
    ];
  });
  return {
    ...block,
    Kind: { case: 'Table', value },
    Default:
      block.Default && TableRows.length
        ? { ...block.Default, TableRows }
        : undefined,
  };
};

// A table's columns after an edit, `kept` giving the index each had (null
// for one just added).
export const setNoteTableColumns = (
  block: NoteBlockDef,
  Columns: PlainMessage<NoteTableColumn>[],
  kept: (number | null)[],
): NoteBlockDef => {
  if (block.Kind.case !== 'Table') {
    return block;
  }
  const value = block.Kind.value;
  return withTableStart(
    block,
    { ...value, Columns },
    sameValue,
    mapCodedValues(value.Columns, Columns, kept, (column) => column.Code),
  );
};

// A table's fixed rows after an edit, as setNoteTableColumns.
export const setNoteTableRows = (
  block: NoteBlockDef,
  Rows: PlainMessage<NoteTableRow>[],
  kept: (number | null)[],
): NoteBlockDef => {
  if (block.Kind.case !== 'Table') {
    return block;
  }
  const value = block.Kind.value;
  return withTableStart(
    block,
    { ...value, Rows },
    mapCodedValues(value.Rows, Rows, kept, (row) => row.Code),
    sameValue,
  );
};

// A table fixes its rows or lets the note add them; the start no longer
// fits, so it goes.
export const setNoteTableAddsRows = (
  block: NoteBlockDef,
  addRows: boolean,
): NoteBlockDef =>
  block.Kind.case === 'Table'
    ? {
        ...block,
        Kind: {
          case: 'Table',
          value: {
            ...block.Kind.value,
            AddRows: addRows || undefined,
            Rows: addRows ? [] : block.Kind.value.Rows,
          },
        },
        Default: undefined,
      }
    : block;

export type NoteCodeProblem = 'missing' | 'format' | 'repeated';

// What scribe refuses in the code of one option or tooth code: none where
// it is `required` (a tooth code), the others carry codes or a code system
// asks for them, one out of the format (the code system's, else the
// neutral one), or one another holds.
export const findNoteCodeProblem = (
  items: { Code: string }[],
  index: number,
  {
    codeSystem,
    required = false,
  }: { codeSystem?: NoteCodeSystem; required?: boolean } = {},
): NoteCodeProblem | null => {
  const { Code } = items[index];
  if (!Code) {
    return required || codeSystem || items.some((item) => item.Code)
      ? 'missing'
      : null;
  }
  if (!isNoteOptionCode(Code, codeSystem)) {
    return 'format';
  }
  return items.some((item, other) => other !== index && item.Code === Code)
    ? 'repeated'
    : null;
};

// The codes the series' active version gives the options or tooth codes of
// one block, by label ignoring case and spacing: scribe keeps each with its
// label in a new active version (`option_code_changed`). A draft started
// from a draft freezes none.
export const listFrozenNoteCodes = (
  source: DocumentTemplate,
  blockId: string,
): Map<string, string> => {
  const block = findNoteBlock(source, blockId)?.block;
  if (source.status !== 'active' || !block) {
    return new Map();
  }
  const coded =
    block.Kind.case === 'Teeth'
      ? block.Kind.value.Codes
      : listNoteBlockOptions(block);
  return new Map(
    coded
      .filter((item) => item.Code)
      .map((item) => [normalizeNoteValue(item.Label), item.Code]),
  );
};

// Options take one value or several. A clinic-list block takes it through
// Multiple.
export type NoteChoiceSelection = 'one' | 'several';

export const NOTE_CHOICE_SELECTIONS: NoteChoiceSelection[] = ['one', 'several'];

export const getNoteChoiceSelection = (
  block: NoteBlockDef,
): NoteChoiceSelection | null => {
  switch (block.Kind.case) {
    case 'Choice':
      return 'one';
    case 'MultiChoice':
      return 'several';
    case 'ListRef':
      return block.Kind.value.Multiple ? 'several' : 'one';
    default:
      return null;
  }
};

// Switching keeps the id, the values and options, "other", the hint, the
// explicit none with its label and the code system, so links and answers
// that name the block stay valid.
export const setNoteChoiceSelection = (
  block: NoteBlockDef,
  selection: NoteChoiceSelection,
): NoteBlockDef => {
  if (getNoteChoiceSelection(block) === selection) {
    return block;
  }
  const Widget = NoteBlockWidget.NoteBlockWidgetButtons;
  switch (block.Kind.case) {
    case 'Choice':
    case 'MultiChoice': {
      const value = { ...block.Kind.value, Widget };
      return {
        ...block,
        Kind:
          selection === 'several'
            ? { case: 'MultiChoice', value }
            : { case: 'Choice', value },
      };
    }
    case 'ListRef':
      return {
        ...block,
        Kind: {
          case: 'ListRef',
          value: {
            ...block.Kind.value,
            Multiple: selection === 'several',
            Widget,
          },
        },
      };
    default:
      return block;
  }
};

// "Convert into a shared list": the block keeps its id, kind of choice,
// "other" and hint, and reads its values from the list, whose items are the
// block's values, so links stay valid.
export const toNoteListRefBlock = (
  block: NoteBlockDef,
  listId: string,
): NoteBlockDef => {
  if (block.Kind.case !== 'Choice' && block.Kind.case !== 'MultiChoice') {
    return block;
  }
  const { OtherAllowed, Label, AllowNone, NoneLabel, CodeSystem } =
    block.Kind.value;
  return {
    ID: block.ID,
    SlotID: block.SlotID,
    Kind: {
      case: 'ListRef',
      value: {
        ListID: listId,
        Multiple: block.Kind.case === 'MultiChoice',
        OtherAllowed,
        Widget: NoteBlockWidget.NoteBlockWidgetButtons,
        Label,
        AllowNone,
        NoneLabel,
        CodeSystem,
      },
    },
  };
};

// "Values from: this block": the list's items become the block's own values,
// so links that name them stay valid; one or several, "other" and the hint
// are kept.
export const fromNoteListRefBlock = (
  block: NoteBlockDef,
  list: NoteList | undefined,
): NoteBlockDef => {
  if (block.Kind.case !== 'ListRef') {
    return block;
  }
  const { Multiple, OtherAllowed, Label, AllowNone, NoneLabel, CodeSystem } =
    block.Kind.value;
  const value = {
    Values: list ? list.Items.map((item) => item.Value) : [],
    OtherAllowed,
    Label,
    Widget: NoteBlockWidget.NoteBlockWidgetButtons,
    Options: [],
    AllowNone,
    NoneLabel,
    CodeSystem,
  };
  return {
    ID: block.ID,
    SlotID: block.SlotID,
    Kind: Multiple ? { case: 'MultiChoice', value } : { case: 'Choice', value },
  };
};

// A list holds values alone: options with codes, print forms, hints or
// pictures stay the block's own.
export const canConvertToNoteList = (block: NoteBlockDef) =>
  (block.Kind.case === 'Choice' || block.Kind.case === 'MultiChoice') &&
  !listNoteBlockOptions(block).length &&
  block.Kind.value.Values.some((value) => value.trim());

// A name to start from: the block's hint, else the wording right in front
// of it ("Анестетик:"), else its section's title.
export const suggestNoteListName = (
  template: DocumentTemplate,
  blockId: string,
) => {
  const place = findNoteBlock(template, blockId);
  if (!place) {
    return '';
  }
  const { Kind } = place.block;
  const label =
    Kind.case === 'Choice' || Kind.case === 'MultiChoice'
      ? Kind.value.Label?.trim()
      : undefined;
  const previous = place.line.Blocks[place.index - 1]?.Kind;
  const wording =
    previous?.case === 'Text'
      ? splitInlineGuidance(previous.value.Value)
          .filter((segment) => !segment.guidance)
          .map((segment) => segment.text)
          .join('')
          .trim()
          .replace(/[\s:–—-]+$/u, '')
      : '';
  return label || wording || place.section.Title;
};

export type NoteLinkTrigger = {
  blockId: string;
  lineId: string;
  sectionTitle: string;
  lineLabel: string;
  values: string[];
};

// Blocks a line may depend on: choice-like blocks of any section that are
// not on the line itself. Scribe checks the rest of the link rules.
export const listNoteLinkTriggers = (
  template: DocumentTemplate,
  lists: NoteList[],
  exceptLineId: string,
): NoteLinkTrigger[] =>
  template.sections.flatMap((section) =>
    section.Lines.filter((line) => line.ID !== exceptLineId).flatMap((line) =>
      line.Blocks.flatMap((block) => {
        const values = getNoteBlockValues(block, lists);
        return values
          ? [
              {
                blockId: block.ID,
                lineId: line.ID,
                sectionTitle: section.Title,
                lineLabel: describeNoteLine(line),
                values,
              },
            ]
          : [];
      }),
    ),
  );

export type PlacedTemplateIssues = {
  bySection: Map<string, TemplateIssue[]>;
  byLine: Map<string, TemplateIssue[]>;
  byBlock: Map<string, TemplateIssue[]>;
  // Issues that name nothing in the draft, e.g. elements scribe minted ids
  // for while refusing the write.
  unplaced: TemplateIssue[];
};

type IssuePlace = 'block' | 'line' | 'section' | 'unplaced';

// An issue is shown at the deepest of its section, line and block that the
// editor shows. The block counts only on the issue's line: a show-when issue
// names the block its condition reads, which may be in another section. A
// line the view filter hides passes its issues to its section.
export const placeTemplateIssues = ({
  template,
  issues,
  shownLineIds,
}: {
  template: DocumentTemplate;
  issues: TemplateIssue[];
  shownLineIds: string[];
}): PlacedTemplateIssues => {
  const sectionIds = new Set(template.sections.map((section) => section.ID));
  const sectionOfLine = new Map(
    template.sections.flatMap((section) =>
      section.Lines.map((line) => [line.ID, section.ID] as const),
    ),
  );
  const lineOfBlock = new Map(
    template.sections.flatMap((section) =>
      section.Lines.flatMap((line) =>
        line.Blocks.map((block) => [block.ID, line.ID] as const),
      ),
    ),
  );
  const shown = new Set(shownLineIds);
  const placeOf = ({
    SectionID,
    LineID,
    BlockID,
  }: TemplateIssue): [IssuePlace, string] => {
    const blockLineId = lineOfBlock.get(BlockID);
    const lineId = LineID || blockLineId || '';
    const lineSectionId = sectionOfLine.get(lineId);
    if (shown.has(lineId) && (!SectionID || SectionID === lineSectionId)) {
      return blockLineId === lineId ? ['block', BlockID] : ['line', lineId];
    }
    const sectionId = SectionID || lineSectionId || '';
    return sectionIds.has(sectionId)
      ? ['section', sectionId]
      : ['unplaced', ''];
  };
  const placed = issues.map((issue) => ({ issue, place: placeOf(issue) }));
  const groupAt = (place: IssuePlace) =>
    placed.reduce(
      (grouped, { issue, place: [issuePlace, id] }) =>
        issuePlace === place
          ? new Map(grouped).set(id, [...(grouped.get(id) ?? []), issue])
          : grouped,
      new Map<string, TemplateIssue[]>(),
    );
  return {
    bySection: groupAt('section'),
    byLine: groupAt('line'),
    byBlock: groupAt('block'),
    unplaced: placed.flatMap(({ issue, place: [issuePlace] }) =>
      issuePlace === 'unplaced' ? [issue] : [],
    ),
  };
};
